import { Checkbox } from '@consigli/shadcn';
import {
  CONFLICT_TYPE_LABELS,
  TENDER_PROCESSOR_CATEGORIES,
  ProcessorPreferenceKey,
  CategoryKey,
} from '@consigli/types';
import { FC, useCallback, useMemo } from 'react';

type CategoryOptions = readonly { key: ProcessorPreferenceKey; translationKey: string }[];

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  RISK: 'Risk',
  INSIGHT: 'Insight',
  GAPS: 'Gaps',
};

interface ConflictTypeSelectorProps {
  selectedConflictTypes?: ProcessorPreferenceKey[];
  onSelectionChange: (selected: ProcessorPreferenceKey[]) => void;
  label?: string;
  description?: string;
  categories?: Partial<Record<CategoryKey, CategoryOptions>>;
}

export const ConflictTypeSelector: FC<ConflictTypeSelectorProps> = ({
  selectedConflictTypes = [],
  onSelectionChange,
  label = 'Select Conflict Types to Filter',
  description = 'Leave empty to show all findings, or select specific conflict types to filter',
  categories = TENDER_PROCESSOR_CATEGORIES,
}) => {
  const selected = useMemo(() => selectedConflictTypes ?? [], [selectedConflictTypes]);

  const handleToggle = useCallback(
    (key: ProcessorPreferenceKey) => {
      if (selected.includes(key)) {
        onSelectionChange(selected.filter((k) => k !== key));
      } else {
        onSelectionChange([...selected, key]);
      }
    },
    [selected, onSelectionChange],
  );

  const handleSelectAllCategory = useCallback(
    (categoryKeys: ProcessorPreferenceKey[]) => {
      const allSelected = categoryKeys.every((key) => selected.includes(key));
      if (allSelected) {
        onSelectionChange(selected.filter((k) => !categoryKeys.includes(k)));
      } else {
        const newSelected = [...selected];
        categoryKeys.forEach((key) => {
          if (!newSelected.includes(key)) {
            newSelected.push(key);
          }
        });
        onSelectionChange(newSelected);
      }
    },
    [selected, onSelectionChange],
  );

  const categoryEntries = Object.entries(categories) as [CategoryKey, CategoryOptions][];

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <p className="text-sm text-gray-500">{description}</p>

      {categoryEntries.map(([categoryKey, options]) => {
        const categoryKeys = options.map((option) => option.key);
        const allSelected = categoryKeys.every((key) => selected.includes(key));

        return (
          <div key={categoryKey} className="border border-gray-200 rounded-md p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {CATEGORY_LABELS[categoryKey]}
              </span>
              <button
                type="button"
                onClick={() => handleSelectAllCategory(categoryKeys)}
                className="cursor-pointer text-xs font-medium text-gray-700 hover:text-gray-900 hover:underline"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {options.map((option) => (
                <div key={option.key} className="flex items-center gap-2">
                  <Checkbox
                    id={`conflict-type-${option.key}`}
                    checked={selected.includes(option.key)}
                    onCheckedChange={() => handleToggle(option.key)}
                  />
                  <label
                    htmlFor={`conflict-type-${option.key}`}
                    className="text-sm text-gray-600 cursor-pointer"
                  >
                    {CONFLICT_TYPE_LABELS[option.key]}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
